import { useState } from 'react';
import type { Consent, ConsentType } from '@repo/domain';
import { tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { Button } from '@repo/ui';

import ConsentToggle from '@shared/components/consent/ConsentToggle';

interface Props {
  testId: TestId;
  expectedConsents: Consent[];
  acceptConsents: (types: ConsentType[]) => Promise<unknown>;
  finalizing: boolean;
}

export default function ConsentsSection({ testId, expectedConsents, acceptConsents, finalizing }: Props) {
  const [acceptedConsents, setAcceptedConsents] = useState<Set<ConsentType>>(new Set());

  const requiredConsentTypes = expectedConsents.filter((consent) => consent.isRequired).map((consent) => consent.type);
  const allRequiredAccepted = requiredConsentTypes.every((type) => acceptedConsents.has(type));

  const handleAccept = async () => {
    if (finalizing) return;
    try {
      await acceptConsents(Array.from(acceptedConsents));
    } catch {
      toast.danger(tr('auth:login_drawer.accept_consents_error'), testId.append('acceptConsentsErrorToast'));
    }
  };

  const handleAcceptAll = async () => {
    if (finalizing) return;
    const allTypes = expectedConsents.map((consent) => consent.type);
    setAcceptedConsents(new Set(allTypes));
    try {
      await acceptConsents(allTypes);
    } catch {
      toast.danger(tr('auth:login_drawer.accept_consents_error'), testId.append('acceptConsentsErrorToast'));
    }
  };

  const handleToggle = (type: ConsentType, value: boolean) => {
    setAcceptedConsents((previous) => {
      const next = new Set(previous);
      if (value) next.add(type);
      else next.delete(type);
      return next;
    });
  };

  return (
    <>
      <h2 className="text-heading text-xl font-semibold">{tr('auth:login_drawer.consents.title')}</h2>
      <p className="text-body text-sm">{tr('auth:login_drawer.consents.subtitle')}</p>

      {expectedConsents.map((consent) => {
        const handleChange = (value: boolean) => handleToggle(consent.type, value);
        return (
          <ConsentToggle
            key={consent.type}
            consent={consent}
            checked={acceptedConsents.has(consent.type)}
            onChange={handleChange}
            testId={testId.append('consentCheckbox').append(consent.type)}
          />
        );
      })}

      <div className="flex gap-2">
        <Button
          color="brand"
          testId={testId.append('acceptConsents')}
          onClick={handleAccept}
          disabled={!allRequiredAccepted || finalizing}
          className="flex-1"
        >
          {tr('auth:login_drawer.consents.accept_button')}
        </Button>
        <Button color="brand" testId={testId.append('acceptAllConsents')} onClick={handleAcceptAll} className="flex-1">
          {tr('auth:login_drawer.consents.accept_all_button')}
        </Button>
      </div>
    </>
  );
}
